import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/api';
import { useAuth } from '@/contexts/AuthContext';

const SessionsPage = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [revokeOthersBusy, setRevokeOthersBusy] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.listSessions(token);
      // Backend liefert { sessions: [...] }
      const arr = Array.isArray(data) ? data : (data.sessions || data.items || []);
      // Nur aktive (nicht revoked) anzeigen
      const active = arr.filter(s => !s.revokedAt);
      setSessions(active.map(s => ({ ...s, isCurrent: s.isCurrent || s.current })));
    } catch (e) {
      setError(e.message || t('sessions.loadError'));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const revoke = async (id) => {
    setRevoking(id);
    try {
      await api.revokeSession(token, id);
      await load();
    } catch (e) {
      setError(e.message || t('sessions.endError'));
    } finally {
      setRevoking(null);
    }
  };

  const revokeOthers = async () => {
    setRevokeOthersBusy(true);
    try {
      await api.revokeOtherSessions(token);
      await load();
    } catch (e) {
      setError(e.message || t('sessions.endOthersError'));
    } finally {
      setRevokeOthersBusy(false);
    }
  };

  const currentId = sessions.find(s => !s.revokedAt && !s.replacedByTokenId && s.isCurrent)?.id || null;

  return (
    <div className="max-w-5xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-4">{t('sessions.title')}</h1>
      <p className="text-sm text-muted-foreground mb-6">{t('sessions.desc')}</p>
      {error && <div className="mb-4 text-red-600 text-sm">{error}</div>}
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={load}
          disabled={loading}
          className="px-3 py-2 text-sm rounded bg-muted hover:bg-muted disabled:opacity-50"
        >{t('sessions.reload')}</button>
        <button
          onClick={revokeOthers}
          disabled={revokeOthersBusy || loading}
          className="px-3 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
        >{t('sessions.endOthers')}</button>
      </div>
      <div className="overflow-x-auto border rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-muted border-b">
            <tr className="text-left">
              <th className="p-2">{t('sessions.thead.current')}</th>
              <th className="p-2">{t('sessions.thead.id')}</th>
              <th className="p-2">{t('sessions.thead.created')}</th>
              <th className="p-2">{t('sessions.thead.lastUsed')}</th>
              <th className="p-2">{t('sessions.thead.expires')}</th>
              <th className="p-2">{t('sessions.thead.ip')}</th>
              <th className="p-2">{t('sessions.thead.userAgent')}</th>
              <th className="p-2">{t('sessions.thead.status')}</th>
              <th className="p-2">{t('sessions.thead.action')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="p-4 text-center text-muted-foreground">{t('sessions.loading')}</td></tr>
            )}
            {!loading && sessions.length === 0 && (
              <tr><td colSpan={9} className="p-4 text-center text-muted-foreground">{t('sessions.none')}</td></tr>
            )}
            {!loading && sessions.map(s => {
              const isCurrent = s.isCurrent || s.id === currentId; // backend could set flag or we infer
              const revoked = !!s.revokedAt;
              const replaced = !!s.replacedByTokenId && !revoked;
              const expired = s.expiresAt && new Date(s.expiresAt) < new Date();
              let status = t('sessions.status.active');
              if (revoked) status = t('sessions.status.ended');
              else if (replaced) status = t('sessions.status.rotated');
              else if (expired) status = t('sessions.status.expired');
              const shortUa = (s.userAgent || '').slice(0, 40) + ((s.userAgent || '').length > 40 ? '…' : '');
              return (
                <tr key={s.id} className="border-b hover:bg-muted">
                  <td className="p-2">
                    {isCurrent && <span className="inline-block w-2 h-2 rounded-full bg-green-600" title={t('sessions.thisSession')}></span>}
                  </td>
                  <td className="p-2 font-mono text-xs" title={s.id}>{s.id.slice(0, 8)}</td>
                  <td className="p-2" title={s.createdAt}>{new Date(s.createdAt).toLocaleString()}</td>
                  <td className="p-2" title={s.lastUsedAt}>{s.lastUsedAt ? new Date(s.lastUsedAt).toLocaleString() : '—'}</td>
                  <td className="p-2" title={s.expiresAt}>{s.expiresAt ? new Date(s.expiresAt).toLocaleString() : '—'}</td>
                  <td className="p-2" title={s.ip || ''}>{s.ip || '—'}</td>
                  <td className="p-2" title={s.userAgent || ''}>{shortUa || '—'}</td>
                  <td className="p-2">
                    <span className={`px-2 py-1 rounded text-xs ${status === 'Aktiv' ? 'bg-green-100 text-green-700' : status === 'Rotiert' ? 'bg-blue-100 text-blue-700' : status === 'Beendet' ? 'bg-red-100 text-red-700' : 'bg-muted text-foreground'}`}>{status}</span>
                  </td>
                  <td className="p-2">
                    {!isCurrent && !revoked && (
                      <button
                        onClick={() => revoke(s.id)}
                        disabled={revoking === s.id}
                        className="px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                      >{t('sessions.end')}</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SessionsPage;
